import React, { useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import validator from 'validator'

import { Grid, Button, Icon, ThemeProvider, lightTheme } from '@rodeo-dev/legos'
import ContactPersonRow from './contact_person_row.jsx'
import IntlMessage from '../../../common/common/intl/intl_message.jsx'
import IntlUtil from '../../../lib/intlutil.jsx'

// It's important that there is always at least one empty contact in the list.
// That's how users add new records. These functions are used to ensure that's the case.
const getDefaultContact = () => [{ name: '', email: '', phone: '' }]
const doesEmptyContactExist = contacts => contacts.some(contact => !(contact.name || contact.email))
const getDefaultContactIfNeeded = contacts => doesEmptyContactExist(contacts) ? [] : getDefaultContact()
const ensureEmptyContactExists = contacts => [...contacts, ...getDefaultContactIfNeeded(contacts)]

const ContactPersonsTab = ({
  contactPersons = [],
  onSaveContactPersons
}) => {
  const sanitizedContactPersons = _.cloneDeep(ensureEmptyContactExists(contactPersons))
  const [contacts, setContacts] = useState(sanitizedContactPersons)

  const [showErrors, setShowErrors] = useState(false)

  const hasValidEmail = contactPerson => {
    const { email } = contactPerson
    return email ? validator.isEmail(email.toString().trim()) : true
  }

  const allEmailsAreValid = () => contacts.every(hasValidEmail)

  const handleContactPersonsFormSave = () => {
    if (allEmailsAreValid()) {
      onSaveContactPersons(contacts)
    } else {
      setShowErrors(() => true)
    }
  }

  const onChangeContactPerson = (name, index, value) => {
    const newContacts = [...contacts]
    newContacts[index][name] = value

    setContacts(newContacts)
  }

  const onFocusContactPerson = () => setContacts(ensureEmptyContactExists)

  const onKeyPressContactPerson = (name, id, ev) => {
    // User press enter or tab
    if (ev.keyCode === 13 || ev.keyCode === 9) {
      setContacts(ensureEmptyContactExists)
    }
  }

  const onDeleteContactPerson = (name, id, ev) => {
    const newContacts = contacts.filter((_, index) => index !== id)
    setContacts(() => ensureEmptyContactExists(newContacts))
  }

  return (
    <div className='form-layout phonesection'>
      <div className='layoutcol layoutcol_full'>
        <div className='formsection'>
          <div className='formheader formheader_hasborder'>
            <Grid container>
              <Grid item sm={6}>
                <h3>
                  <IntlMessage ident='contact.form.contacts' />
                </h3>
              </Grid>
            </Grid>
          </div>
          {contacts.map((contact, index) => {
            const showError = showErrors && !hasValidEmail(contact)

            return (
              <ContactPersonRow
                key={index}
                contact={contact}
                index={index}
                showError={showError}
                onDeleteContactPerson={onDeleteContactPerson}
                onChangeContactPerson={onChangeContactPerson}
                onKeyPressContactPerson={onKeyPressContactPerson}
                onFocusContactPerson={onFocusContactPerson}
              />
            )
          })}
          <ThemeProvider theme={lightTheme}>
            <Grid container justify='flex-end'>
              <Button
                color='secondary'
                onClick={handleContactPersonsFormSave}
                disabled={false}
                size='small'
                startIcon={<Icon icon='check' size='small' color='inherit' />}
              >
                {IntlUtil.convertMessage('common.form.save')}
              </Button>
            </Grid>
          </ThemeProvider>
        </div>
      </div>
    </div>
  )
}

export default ContactPersonsTab

ContactPersonsTab.propTypes = {
  contactPersons: PropTypes.array.isRequired,
  onSaveContactPersons: PropTypes.func.isRequired
}
